import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

// AJUSTAR ESTE IMPORT SEGÚN TU PROYECTO
import { db } from "../firebase";

const LABELS = {
  pedido_turno: "Pedido de turno",
  urgencia: "Urgencia",
  consulta_medica: "Consulta médica",
  consulta_administrativa: "Consulta administrativa",
  seguimiento_paciente: "Seguimiento de paciente",
  resultado_estudio: "Resultado de estudio",
  medicacion: "Medicación",
  cirugia: "Cirugía",
  internacion: "Internación",
  precio_presupuesto: "Precio / presupuesto",
  reclamo: "Reclamo",
  otro: "Otro",

  resuelto_en_llamada: "Resuelto en llamada",
  derivado_a_whatsapp: "Derivado a WhatsApp",
  derivado_a_otro_canal: "Derivado a otro canal",
  requiere_seguimiento: "Requiere seguimiento",
  no_resuelto: "No resuelto",
  llamada_cortada: "Llamada cortada",
  informativo: "Informativo",

  cambio_de_canal: "Cambio de canal",
  demora: "Demora",
  falta_de_informacion: "Falta de información",
  turno_no_resuelto: "Turno no resuelto",
  audio_confuso: "Audio confuso",

  bajo: "Bajo",
  medio: "Medio",
  alto: "Alto",
  emergencia: "Emergencia",
  no_aplica: "No aplica",

  buena: "Buena",
  media: "Media",
  mala: "Mala",
  no_evaluable: "No evaluable",

  positivo: "Positivo",
  neutral: "Neutral",
  negativo: "Negativo",
  frustrado: "Frustrado",
  ansioso: "Ansioso",
  desconocido: "Desconocido",

  whatsapp: "WhatsApp",
  presencial: "Presencial",
  email: "Email",
};

const PIE_COLORS = [
  "#575fd5",
  "#6f77ea",
  "#8c92f2",
  "#a5abff",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#64748b",
  "#14b8a6",
  "#a855f7",
];

function label(value) {
  if (value === null || value === undefined || value === "") return "Sin dato";
  return LABELS[value] || String(value).replaceAll("_", " ");
}

function pct(value, total) {
  if (!total) return "0%";
  return `${Math.round((value / total) * 100)}%`;
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return "Sin dato";
  }

  const total = Number(seconds);
  if (total < 60) return `${Math.round(total)}s`;

  const min = Math.floor(total / 60);
  const sec = Math.round(total % 60);

  return `${min}m ${sec}s`;
}

function formatHours(seconds) {
  if (!seconds) return "0h";
  const hours = seconds / 3600;
  return `${hours.toFixed(1)}h`;
}

function countBy(items, getter) {
  const map = {};

  for (const item of items) {
    const key = getter(item) || "sin_dato";
    map[key] = (map[key] || 0) + 1;
  }

  return Object.entries(map)
    .map(([key, value]) => ({
      key,
      name: label(key),
      value,
    }))
    .sort((a, b) => b.value - a.value);
}

function topArrayValues(items, getter, limit = 10) {
  const map = {};

  for (const item of items) {
    const values = getter(item);

    if (!Array.isArray(values)) continue;

    for (const rawValue of values) {
      const value = String(rawValue || "").trim();
      if (!value) continue;
      map[value] = (map[value] || 0) + 1;
    }
  }

  return Object.entries(map)
    .map(([key, value]) => ({
      key,
      name: key,
      value,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

function Badge({ children, tone = "default" }) {
  const tones = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-indigo-50 text-indigo-700 border-indigo-200",
    orange: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    gray: "bg-gray-50 text-gray-600 border-gray-200",
    purple: "bg-violet-50 text-violet-700 border-violet-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </span>
  );
}

function getResultTone(value) {
  if (value === "resuelto_en_llamada") return "green";
  if (value === "derivado_a_whatsapp") return "blue";
  if (value === "no_resuelto" || value === "llamada_cortada") return "red";
  if (value === "requiere_seguimiento") return "orange";
  return "gray";
}

function getImpactTone(value) {
  if (value === "alto") return "red";
  if (value === "medio") return "orange";
  if (value === "bajo") return "green";
  return "gray";
}

function MetricCard({ title, value, subtitle, tone = "default" }) {
  const toneClasses = {
    default: "bg-white border-slate-200",
    blue: "bg-indigo-50 border-indigo-100",
    green: "bg-emerald-50 border-emerald-100",
    orange: "bg-amber-50 border-amber-100",
    red: "bg-rose-50 border-rose-100",
    purple: "bg-violet-50 border-violet-100",
  };

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${toneClasses[tone] || toneClasses.default}`}>
      <p className="text-sm text-slate-500">{title}</p>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
      {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function BurgessCallsDashboard() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState("");
  const [selectedCall, setSelectedCall] = useState(null);

  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    agente: "todos",
    motivoPrincipal: "todos",
    resultadoLlamada: "todos",
    impactoOperativo: "todos",
    soloAnalizables: true,
    soloFriccion: false,
    soloWhatsapp: false,
    search: "",
  });

  async function loadCalls() {
    setLoading(true);
    setLoadingError("");

    try {
      const q = query(
        collection(db, "companies", "Burgess", "call_analysis"),
        where("status", "==", "done")
      );

      const snap = await getDocs(q);

      const items = snap.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          ...data,
          dashboard: data.dashboard || {},
          analysis: data.analysis || {},
          transcription: data.transcription || {},
          audio: data.audio || {},
          metadata: data.metadata || {},
        };
      });

      setCalls(items);
    } catch (error) {
      console.error("Error cargando llamadas:", error);
      setLoadingError(error.message || "No se pudieron cargar las llamadas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCalls();
  }, []);

  const filterOptions = useMemo(() => {
    const agentes = Array.from(
      new Set(
        calls
          .map((call) => call.dashboard?.agente)
          .filter(Boolean)
      )
    ).sort();

    return {
      agentes,
    };
  }, [calls]);

  const filteredCalls = useMemo(() => {
    const search = normalizeText(filters.search);

    return calls.filter((call) => {
      const d = call.dashboard || {};
      const a = call.analysis || {};

      if (filters.dateFrom && d.fecha && d.fecha < filters.dateFrom) return false;
      if (filters.dateTo && d.fecha && d.fecha > filters.dateTo) return false;

      if (filters.agente !== "todos" && d.agente !== filters.agente) return false;
      if (filters.motivoPrincipal !== "todos" && d.motivoPrincipal !== filters.motivoPrincipal) return false;
      if (filters.resultadoLlamada !== "todos" && d.resultadoLlamada !== filters.resultadoLlamada) return false;
      if (filters.impactoOperativo !== "todos" && d.impactoOperativo !== filters.impactoOperativo) return false;

      if (filters.soloAnalizables && Number(d.duracionSegundos || 0) < 10) return false;
      if (filters.soloFriccion && !d.huboFriccion) return false;
      if (filters.soloWhatsapp && !d.derivadoAWhatsapp) return false;

      if (search) {
        const text = normalizeText([
          d.llamante,
          d.telefonoCliente,
          d.clienteNombre,
          d.mascotaNombre,
          d.tipoMascota,
          d.profesionalMencionado,
          d.motivoPrincipal,
          d.resultadoLlamada,
          d.notasDashboard,
          a.resumen,
          ...(d.palabrasClave || []),
          ...(d.temasDetectados || []),
        ].join(" "));

        if (!text.includes(search)) return false;
      }

      return true;
    });
  }, [calls, filters]);

  const metrics = useMemo(() => {
    const total = filteredCalls.length;
    const analizables = filteredCalls.filter((c) => Number(c.dashboard?.duracionSegundos || 0) >= 10);
    const totalAnalizables = analizables.length;

    const resueltas = filteredCalls.filter((c) => c.dashboard?.resueltoEnLlamada).length;
    const whatsapp = filteredCalls.filter((c) => c.dashboard?.derivadoAWhatsapp).length;
    const friccion = filteredCalls.filter((c) => c.dashboard?.huboFriccion).length;
    const accionCliente = filteredCalls.filter((c) => c.dashboard?.requiereAccionPosteriorCliente).length;
    const seguimiento = filteredCalls.filter((c) => c.dashboard?.requiereSeguimiento).length;
    const urgencias = filteredCalls.filter(
      (c) =>
        c.dashboard?.motivoPrincipal === "urgencia" ||
        c.dashboard?.nivelUrgencia === "alto" ||
        c.dashboard?.nivelUrgencia === "emergencia"
    ).length;
    const noResueltas = filteredCalls.filter((c) => c.dashboard?.resueltoEnLlamada === false).length;
    const impactoAlto = filteredCalls.filter((c) => c.dashboard?.impactoOperativo === "alto").length;

    const totalSeconds = filteredCalls.reduce((acc, c) => acc + Number(c.dashboard?.duracionSegundos || 0), 0);
    const avgSeconds = total ? totalSeconds / total : 0;

    const turnosWhatsapp = filteredCalls.filter(
      (c) => c.dashboard?.motivoPrincipal === "pedido_turno" && c.dashboard?.derivadoAWhatsapp
    ).length;

    const consultasWhatsapp = filteredCalls.filter(
      (c) => c.dashboard?.motivoPrincipal === "consulta_medica" && c.dashboard?.derivadoAWhatsapp
    ).length;

    const cambioCanal = filteredCalls.filter(
      (c) => c.dashboard?.tipoFriccionPrincipal === "cambio_de_canal"
    ).length;

    return {
      total,
      totalAnalizables,
      resueltas,
      whatsapp,
      friccion,
      accionCliente,
      seguimiento,
      urgencias,
      noResueltas,
      impactoAlto,
      totalSeconds,
      avgSeconds,
      turnosWhatsapp,
      consultasWhatsapp,
      cambioCanal,
    };
  }, [filteredCalls]);

  const charts = useMemo(() => {
    const byMotivo = countBy(filteredCalls, (c) => c.dashboard?.motivoPrincipal);
    const byResultado = countBy(filteredCalls, (c) => c.dashboard?.resultadoLlamada);
    const byAgente = countBy(filteredCalls, (c) => c.dashboard?.agente);
    const byImpacto = countBy(filteredCalls, (c) => c.dashboard?.impactoOperativo);
    const byFriccion = countBy(filteredCalls, (c) => c.dashboard?.tipoFriccionPrincipal);
    const byCanal = countBy(filteredCalls, (c) => c.dashboard?.canalDerivacion);
    const byUrgencia = countBy(filteredCalls, (c) => c.dashboard?.nivelUrgencia);
    const bySentimiento = countBy(filteredCalls, (c) => c.dashboard?.sentimientoCliente);
    const byCalidad = countBy(filteredCalls, (c) => c.dashboard?.calidadAtencion);

    const byDayMap = {};

    for (const call of filteredCalls) {
      const d = call.dashboard || {};
      const fecha = d.fecha || "Sin fecha";

      if (!byDayMap[fecha]) {
        byDayMap[fecha] = {
          fecha,
          total: 0,
          resueltas: 0,
          whatsapp: 0,
          friccion: 0,
          noResueltas: 0,
        };
      }

      byDayMap[fecha].total += 1;
      if (d.resueltoEnLlamada) byDayMap[fecha].resueltas += 1;
      if (d.derivadoAWhatsapp) byDayMap[fecha].whatsapp += 1;
      if (d.huboFriccion) byDayMap[fecha].friccion += 1;
      if (d.resueltoEnLlamada === false) byDayMap[fecha].noResueltas += 1;
    }

    const byDay = Object.values(byDayMap).sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));

    const topPalabras = topArrayValues(filteredCalls, (c) => c.dashboard?.palabrasClave, 12);
    const topTemas = topArrayValues(filteredCalls, (c) => c.dashboard?.temasDetectados, 12);

    const topProfesionales = countBy(filteredCalls, (c) => {
      const value = c.dashboard?.profesionalMencionado;
      if (!value || value === "desconocido") return null;
      return value;
    }).slice(0, 10);

    const topTiposMascota = countBy(filteredCalls, (c) => c.dashboard?.tipoMascota).slice(0, 10);

    return {
      byMotivo,
      byResultado,
      byAgente,
      byImpacto,
      byFriccion,
      byCanal,
      byUrgencia,
      bySentimiento,
      byCalidad,
      byDay,
      topPalabras,
      topTemas,
      topProfesionales,
      topTiposMascota,
    };
  }, [filteredCalls]);

  const findings = useMemo(() => {
    const mainMotivo = charts.byMotivo[0];
    const mainNoResolution = countBy(
      filteredCalls.filter((c) => c.dashboard?.motivoNoResolucion),
      (c) => c.dashboard?.motivoNoResolucion
    )[0];

    const mainAgent = charts.byAgente[0];

    const oportunidadesMap = {};
    const automatizacionesMap = {};

    for (const call of filteredCalls) {
      const a = call.analysis || {};

      for (const item of a.oportunidadesMejora || []) {
        const key = String(item || "").trim();
        if (key) oportunidadesMap[key] = (oportunidadesMap[key] || 0) + 1;
      }

      for (const item of a.automatizacionesSugeridas || []) {
        const key = String(item || "").trim();
        if (key) automatizacionesMap[key] = (automatizacionesMap[key] || 0) + 1;
      }
    }

    const topOportunidades = Object.entries(oportunidadesMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const topAutomatizaciones = Object.entries(automatizacionesMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      mainMotivo,
      mainNoResolution,
      mainAgent,
      topOportunidades,
      topAutomatizaciones,
    };
  }, [filteredCalls, charts]);

  function setFilterValue(key, value) {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="h-6 w-64 animate-pulse rounded bg-slate-200" />
            <div className="mt-4 h-4 w-96 animate-pulse rounded bg-slate-100" />
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl border border-rose-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-bold text-rose-700">Error cargando dashboard</h2>
          <p className="mt-2 text-slate-600">{loadingError}</p>
          <button
            onClick={loadCalls}
            className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-950 md:text-3xl">
                Dashboard de Atención Telefónica — Burgess
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Análisis automático de llamadas procesadas con IA.
              </p>
            </div>

            <button
              onClick={loadCalls}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            >
              Actualizar datos
            </button>
          </div>
        </header>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4 lg:grid-cols-6">
            <div>
              <label className="text-xs font-medium text-slate-500">Desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilterValue("dateFrom", e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">Hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilterValue("dateTo", e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
              />
            </div>

            <SelectFilter
              label="Agente"
              value={filters.agente}
              onChange={(value) => setFilterValue("agente", value)}
              options={[
                { value: "todos", label: "Todos" },
                ...filterOptions.agentes.map((agente) => ({
                  value: agente,
                  label: agente,
                })),
              ]}
            />

            <SelectFilter
              label="Motivo"
              value={filters.motivoPrincipal}
              onChange={(value) => setFilterValue("motivoPrincipal", value)}
              options={[
                "todos",
                "pedido_turno",
                "urgencia",
                "consulta_medica",
                "consulta_administrativa",
                "seguimiento_paciente",
                "resultado_estudio",
                "medicacion",
                "cirugia",
                "internacion",
                "precio_presupuesto",
                "reclamo",
                "otro",
              ].map((value) => ({ value, label: value === "todos" ? "Todos" : label(value) }))}
            />

            <SelectFilter
              label="Resultado"
              value={filters.resultadoLlamada}
              onChange={(value) => setFilterValue("resultadoLlamada", value)}
              options={[
                "todos",
                "resuelto_en_llamada",
                "derivado_a_whatsapp",
                "derivado_a_otro_canal",
                "requiere_seguimiento",
                "no_resuelto",
                "llamada_cortada",
                "informativo",
                "otro",
              ].map((value) => ({ value, label: value === "todos" ? "Todos" : label(value) }))}
            />

            <SelectFilter
              label="Impacto"
              value={filters.impactoOperativo}
              onChange={(value) => setFilterValue("impactoOperativo", value)}
              options={["todos", "bajo", "medio", "alto"].map((value) => ({
                value,
                label: value === "todos" ? "Todos" : label(value),
              }))}
            />

            <div className="md:col-span-2 lg:col-span-3">
              <label className="text-xs font-medium text-slate-500">Buscar</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilterValue("search", e.target.value)}
                placeholder="Teléfono, cliente, mascota, profesional, palabra clave..."
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
              />
            </div>

            <ToggleFilter
              label="Solo analizables"
              checked={filters.soloAnalizables}
              onChange={(checked) => setFilterValue("soloAnalizables", checked)}
            />

            <ToggleFilter
              label="Con fricción"
              checked={filters.soloFriccion}
              onChange={(checked) => setFilterValue("soloFriccion", checked)}
            />

            <ToggleFilter
              label="Derivadas a WhatsApp"
              checked={filters.soloWhatsapp}
              onChange={(checked) => setFilterValue("soloWhatsapp", checked)}
            />
          </div>
        </section>

        {calls.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Todavía no hay llamadas procesadas</h2>
            <p className="mt-2 text-slate-500">
              Cuando existan documentos con status done en Firestore, van a aparecer acá.
            </p>
          </div>
        ) : null}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <MetricCard title="Llamadas" value={metrics.total} subtitle={`${calls.length} cargadas`} />
          <MetricCard title="Analizables" value={metrics.totalAnalizables} subtitle="Duración ≥ 10s" />
          <MetricCard title="% resueltas" value={pct(metrics.resueltas, metrics.total)} subtitle={`${metrics.resueltas} llamadas`} tone="green" />
          <MetricCard title="% WhatsApp" value={pct(metrics.whatsapp, metrics.total)} subtitle={`${metrics.whatsapp} derivadas`} tone="blue" />
          <MetricCard title="% fricción" value={pct(metrics.friccion, metrics.total)} subtitle={`${metrics.friccion} casos`} tone="orange" />
          <MetricCard title="Duración prom." value={formatDuration(metrics.avgSeconds)} subtitle={`Total ${formatHours(metrics.totalSeconds)}`} />
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <MetricCard title="Turnos a WhatsApp" value={metrics.turnosWhatsapp} tone="blue" />
          <MetricCard title="Consultas a WhatsApp" value={metrics.consultasWhatsapp} tone="blue" />
          <MetricCard title="Cambio de canal" value={metrics.cambioCanal} tone="orange" />
          <MetricCard title="Acción cliente" value={metrics.accionCliente} tone="purple" />
          <MetricCard title="Seguimiento interno" value={metrics.seguimiento} tone="orange" />
          <MetricCard title="Urgencias" value={metrics.urgencias} tone="red" />
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ChartCard title="Llamadas por día" className="lg:col-span-2">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.byDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#575fd5" strokeWidth={2} />
                  <Line type="monotone" dataKey="whatsapp" name="WhatsApp" stroke="#2563eb" strokeWidth={2} />
                  <Line type="monotone" dataKey="resueltas" name="Resueltas" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="friccion" name="Fricción" stroke="#f59e0b" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Motivos principales">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={charts.byMotivo.slice(0, 8)}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={105}
                    label
                  >
                    {charts.byMotivo.slice(0, 8).map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <BarChartCard title="Resultados" data={charts.byResultado} />
          <BarChartCard title="Agentes" data={charts.byAgente} />
          <BarChartCard title="Impacto operativo" data={charts.byImpacto} />
          <BarChartCard title="Fricciones" data={charts.byFriccion} />
          <BarChartCard title="Canales de derivación" data={charts.byCanal} />
          <BarChartCard title="Nivel de urgencia" data={charts.byUrgencia} />
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ChartCard title="Hallazgos automáticos">
            <div className="space-y-3 text-sm">
              <Finding label="Principal motivo" value={findings.mainMotivo ? `${findings.mainMotivo.name} (${findings.mainMotivo.value})` : "Sin dato"} />
              <Finding label="Principal causa de no resolución" value={findings.mainNoResolution ? `${findings.mainNoResolution.name} (${findings.mainNoResolution.value})` : "Sin dato"} />
              <Finding label="Agente con más llamadas" value={findings.mainAgent ? `${findings.mainAgent.name} (${findings.mainAgent.value})` : "Sin dato"} />
              <Finding label="% derivadas a WhatsApp" value={pct(metrics.whatsapp, metrics.total)} />
              <Finding label="% con fricción" value={pct(metrics.friccion, metrics.total)} />
            </div>
          </ChartCard>

          <ChartCard title="Top oportunidades de mejora">
            <RankList items={findings.topOportunidades} emptyText="Sin oportunidades detectadas" />
          </ChartCard>

          <ChartCard title="Top automatizaciones sugeridas">
            <RankList items={findings.topAutomatizaciones} emptyText="Sin automatizaciones sugeridas" />
          </ChartCard>
        </section>

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <BarChartCard title="Top palabras clave" data={charts.topPalabras} />
          <BarChartCard title="Top temas detectados" data={charts.topTemas} />
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center">
            <div>
              <h3 className="font-semibold text-slate-900">Llamadas procesadas</h3>
              <p className="text-sm text-slate-500">
                Mostrando {filteredCalls.length} de {calls.length} llamadas.
              </p>
            </div>
          </div>

          <div className="max-h-[680px] overflow-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Agente</th>
                  <th className="px-4 py-3">Teléfono</th>
                  <th className="px-4 py-3">Cliente / Mascota</th>
                  <th className="px-4 py-3">Motivo</th>
                  <th className="px-4 py-3">Resultado</th>
                  <th className="px-4 py-3">Impacto</th>
                  <th className="px-4 py-3">Duración</th>
                  <th className="px-4 py-3">Nota</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredCalls.map((call) => {
                  const d = call.dashboard || {};

                  return (
                    <tr
                      key={call.id}
                      onClick={() => setSelectedCall(call)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="font-medium text-slate-900">{d.fecha || "Sin fecha"}</div>
                        <div className="text-xs text-slate-500">{d.hora || ""}</div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {d.agente || "Sin dato"}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {d.telefonoCliente || d.llamante || "Sin dato"}
                      </td>

                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{d.clienteNombre || "Sin cliente"}</div>
                        <div className="text-xs text-slate-500">
                          {d.mascotaNombre || "Sin mascota"} · {label(d.tipoMascota)}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <Badge tone="purple">{label(d.motivoPrincipal)}</Badge>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <Badge tone={getResultTone(d.resultadoLlamada)}>
                            {label(d.resultadoLlamada)}
                          </Badge>
                          {d.derivadoAWhatsapp ? <Badge tone="blue">WhatsApp</Badge> : null}
                          {d.huboFriccion ? <Badge tone="orange">Fricción</Badge> : null}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <Badge tone={getImpactTone(d.impactoOperativo)}>
                          {label(d.impactoOperativo)}
                        </Badge>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                        {formatDuration(d.duracionSegundos)}
                      </td>

                      <td className="max-w-xs px-4 py-3 text-slate-600">
                        <div className="line-clamp-2">{d.notasDashboard || d.motivoNoResolucion || "-"}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {selectedCall ? (
        <CallDetailModal
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
        />
      ) : null}
    </div>
  );
}

function SelectFilter({ label: title, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500">{title}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ToggleFilter({ label: title, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      {title}
    </label>
  );
}

function Finding({ label: title, value }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="mt-1 font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function RankList({ items, emptyText }) {
  if (!items || !items.length) {
    return <p className="text-sm text-slate-500">{emptyText}</p>;
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${item.name}-${index}`} className="flex gap-3 rounded-xl bg-slate-50 p-3">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
            {index + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900">{item.name}</div>
            <div className="text-xs text-slate-500">{item.value} casos</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BarChartCard({ title, data }) {
  const safeData = (data || []).slice(0, 10);

  return (
    <ChartCard title={title}>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={safeData} layout="vertical" margin={{ left: 30, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <Bar dataKey="value" name="Cantidad" fill="#575fd5" radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function CallDetailModal({ call, onClose }) {
  const d = call.dashboard || {};
  const a = call.analysis || {};
  const t = call.transcription || {};
  const audio = call.audio || {};

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40">
      <div className="h-full w-full max-w-3xl overflow-auto bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-950">
                Detalle de llamada
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {d.fecha || "Sin fecha"} {d.hora || ""} · {d.agente || "Sin agente"}
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cerrar
            </button>
          </div>
        </div>

        <div className="space-y-5 p-5">
          <div className="flex flex-wrap gap-2">
            <Badge tone="purple">{label(d.motivoPrincipal)}</Badge>
            <Badge tone={getResultTone(d.resultadoLlamada)}>
              {label(d.resultadoLlamada)}
            </Badge>
            <Badge tone={getImpactTone(d.impactoOperativo)}>
              Impacto {label(d.impactoOperativo)}
            </Badge>
            {d.derivadoAWhatsapp ? <Badge tone="blue">Derivado a WhatsApp</Badge> : null}
            {d.huboFriccion ? <Badge tone="orange">{label(d.tipoFriccionPrincipal)}</Badge> : null}
          </div>

          <DetailSection title="Resumen">
            <p className="text-sm leading-6 text-slate-700">
              {a.resumen || d.notasDashboard || "Sin resumen."}
            </p>
          </DetailSection>

          <DetailSection title="Datos principales">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <DetailItem label="Teléfono" value={d.telefonoCliente || d.llamante} />
              <DetailItem label="Cliente" value={d.clienteNombre} />
              <DetailItem label="Mascota" value={d.mascotaNombre} />
              <DetailItem label="Tipo mascota" value={label(d.tipoMascota)} />
              <DetailItem label="Profesional" value={d.profesionalMencionado} />
              <DetailItem label="Duración" value={formatDuration(d.duracionSegundos)} />
              <DetailItem label="Canal derivación" value={label(d.canalDerivacion)} />
              <DetailItem label="Nivel urgencia" value={label(d.nivelUrgencia)} />
              <DetailItem label="Sentimiento" value={label(d.sentimientoCliente)} />
              <DetailItem label="Calidad atención" value={label(d.calidadAtencion)} />
              <DetailItem label="Calidad transcripción" value={label(a.calidadTranscripcion)} />
              <DetailItem label="Confianza análisis" value={a.confianzaAnalisis !== undefined ? `${Math.round(a.confianzaAnalisis * 100)}%` : "Sin dato"} />
            </div>
          </DetailSection>

          <DetailSection title="Estado operativo">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <BooleanItem label="Resuelto en llamada" value={d.resueltoEnLlamada} />
              <BooleanItem label="Derivado a WhatsApp" value={d.derivadoAWhatsapp} />
              <BooleanItem label="Requiere acción del cliente" value={d.requiereAccionPosteriorCliente} />
              <BooleanItem label="Requiere seguimiento interno" value={d.requiereSeguimiento} />
              <BooleanItem label="Hubo fricción" value={d.huboFriccion} />
            </div>

            {d.motivoNoResolucion ? (
              <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                <strong>Motivo de no resolución:</strong> {d.motivoNoResolucion}
              </div>
            ) : null}
          </DetailSection>

          <DetailSection title="Fricciones detectadas">
            <SimpleList items={a.fricciones} emptyText="No se detectaron fricciones." />
          </DetailSection>

          <DetailSection title="Oportunidades de mejora">
            <SimpleList items={a.oportunidadesMejora} emptyText="No se detectaron oportunidades." />
          </DetailSection>

          <DetailSection title="Automatizaciones sugeridas">
            <SimpleList items={a.automatizacionesSugeridas} emptyText="No hay automatizaciones sugeridas." />
          </DetailSection>

          <DetailSection title="Temas y palabras clave">
            <div className="space-y-3">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Temas
                </div>
                <div className="flex flex-wrap gap-2">
                  {(d.temasDetectados || []).length ? (
                    d.temasDetectados.map((item) => (
                      <Badge key={item} tone="gray">{item}</Badge>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">Sin temas.</span>
                  )}
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Palabras clave
                </div>
                <div className="flex flex-wrap gap-2">
                  {(d.palabrasClave || []).length ? (
                    d.palabrasClave.map((item) => (
                      <Badge key={item} tone="gray">{item}</Badge>
                    ))
                  ) : (
                    <span className="text-sm text-slate-500">Sin palabras clave.</span>
                  )}
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Roles inferidos">
            <pre className="overflow-auto rounded-xl bg-slate-950 p-4 text-xs text-slate-100">
              {JSON.stringify(a.rolesInferidos || {}, null, 2)}
            </pre>
          </DetailSection>

          <DetailSection title="Transcripción por hablante">
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">
              {t.readableTextBySpeaker || "Sin transcripción."}
            </pre>
          </DetailSection>

          <DetailSection title="Audio">
            <div className="grid grid-cols-1 gap-3">
              <DetailItem label="Filename" value={audio.filename} />
              <DetailItem label="Storage path" value={audio.storagePath} />
              <DetailItem label="Bucket" value={audio.storageBucket} />
              <DetailItem label="Tamaño" value={audio.sizeBytes ? `${audio.sizeBytes} bytes` : "Sin dato"} />
              <DetailItem label="Source type" value={audio.sourceType} />
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-3 font-semibold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

function DetailItem({ label: title, value }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="mt-1 break-words text-sm font-medium text-slate-900">
        {value || "Sin dato"}
      </div>
    </div>
  );
}

function BooleanItem({ label: title, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
      <div className="text-sm font-medium text-slate-800">{title}</div>
      <Badge tone={value ? "green" : "gray"}>{value ? "Sí" : "No"}</Badge>
    </div>
  );
}

function SimpleList({ items, emptyText }) {
  if (!items || !items.length) {
    return <p className="text-sm text-slate-500">{emptyText}</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
          {item}
        </li>
      ))}
    </ul>
  );
}